import React, { useState } from 'react';
import { X, Edit3, RefreshCw, AlertTriangle } from 'lucide-react';

// Renames a client. This cascades server-side to EVERY table that stores
// clientName — Employee, Department, MedicalInsuranceTier,
// ClientSplashScreen, ArchivedEmployee, DocumentRequest, OfficialHoliday,
// CompanyConfiguration (+ its archive), CompanyModulePermission,
// ClientCodePolicy, AdminLevel, WorkflowDefinition, ReportGenerationHistory,
// ScheduledReport, AttendanceDevice — all in a single transaction (see
// configurationService.renameClient). It is the ONLY way clientName can
// ever change once employees exist under a client; the employee edit forms
// (web + mobile) and every other write path have that field locked down
// specifically so this is the single place it happens.
const RenameCompanyModal = ({ theme, styles, currentName, onRename, onClose }) => {
  const [newName, setNewName] = useState('');
  const [renaming, setRenaming] = useState(false);

  const labelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: theme === 'light' ? '#374151' : 'rgba(255, 255, 255, 0.9)',
    marginBottom: '8px',
  };

  const handleRename = async () => {
    setRenaming(true);
    const success = await onRename(currentName, newName);
    setRenaming(false);
    if (success) {
      setNewName('');
      onClose();
    }
  };

  return (
    <div style={styles.modal} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>Rename Client</h3>
          <button style={styles.closeButton} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div>
          <label style={labelStyle}>Current Name</label>
          <input
            type="text"
            value={currentName || ''}
            disabled
            style={{ ...styles.input, opacity: 0.6, cursor: 'not-allowed', marginBottom: '16px' }}
          />
        </div>

        <div>
          <label style={labelStyle}>New Name</label>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Enter the new client name..."
            style={styles.input}
            autoFocus
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !renaming && newName.trim()) handleRename();
            }}
          />
        </div>

        <div style={{
          display: 'flex',
          gap: '10px',
          alignItems: 'flex-start',
          background: theme === 'light' ? 'rgba(245,158,11,0.08)' : 'rgba(245,158,11,0.12)',
          border: theme === 'light' ? '1px dashed rgba(245,158,11,0.35)' : '1px dashed rgba(245,158,11,0.4)',
          borderRadius: '10px',
          padding: '12px 14px',
          marginTop: '16px',
        }}>
          <AlertTriangle size={16} style={{ color: '#f59e0b', flexShrink: 0, marginTop: '2px' }} />
          <p style={{ fontSize: '12.5px', lineHeight: 1.5, margin: 0, color: theme === 'light' ? '#78350f' : 'rgba(255,255,255,0.75)' }}>
            This immediately renames the client everywhere — every employee record,
            company configuration, admin level, workflow definition, code policy,
            splash screen, and every other table scoped to this client. This cannot
            be an accidental typo fix; double-check the spelling before confirming.
          </p>
        </div>

        <div style={styles.modalButtons}>
          <button style={styles.cancelButton} onClick={onClose} disabled={renaming}>
            Cancel
          </button>
          <button
            style={styles.submitButton}
            onClick={handleRename}
            disabled={renaming || !newName.trim()}
          >
            {renaming ? (
              <>
                <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                Renaming...
              </>
            ) : (
              <>
                <Edit3 size={16} />
                Rename Client
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RenameCompanyModal;
